// The River as an accessible text list: the current slot and what comes next, with times in the
// listener's local time zone.
import { useMemo } from "react";
import { wizardLabel } from "@eb/shared";
import { clock } from "../stream";
import { t } from "../i18n";
import type { RiverPlayer } from "./player";
import { formatClock, slotAt, slotsBetween, type SlotRef } from "./slots";
import { useSlotData } from "./useSlotData";
import { useMyPlays } from "../myPlays";

const { SLOT_MS } = clock;

export function RiverList({ player, onOpen }: { player: RiverPlayer; onOpen: (s: SlotRef) => void }) {
  const epoch = player.epochMs();
  const current = slotAt(player.worldNow(), epoch);
  const slots = useMemo(
    () => slotsBetween(current.startMs - 2 * SLOT_MS, current.startMs + 30 * SLOT_MS, epoch),
    [current.startMs, epoch],
  );
  const data = useSlotData(player, slots, false);
  const myIds = useMyPlays((s) => s.loopIds);
  return (
    <table className="river-list">
      <caption className="sr-only">Lineup around the current slot</caption>
      <thead>
        <tr>
          <th scope="col">Time</th>
          <th scope="col">Song by</th>
          <th scope="col">Wizard</th>
          <th scope="col">
            <span className="sr-only">Details</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {slots.map((s) => {
          const e = data.get(s.key)?.entry;
          const isNow = s.key === current.key;
          return (
            <tr key={s.key} className={isNow ? "now" : ""} aria-current={isNow ? "true" : undefined}>
              <td>
                {formatClock(s.startMs)}
                {isNow && <span className="chip now-chip">{t("river.nowPlaying")}</span>}
              </td>
              <td>
                {e ? (e.author ?? t("river.formerContributor")) : "…"}
                {e?.kind === "human" && myIds.has(e.loopId) && <span className="chip mine-chip">Your song</span>}
              </td>
              <td>{e ? (wizardLabel(e) ?? "—") : ""}</td>
              <td>
                <button type="button" className="btn btn-small btn-ghost" onClick={() => onOpen(s)}>
                  Details
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
